import { useState } from 'react';
import { Loader2, Play, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATUS_STYLES = {
  200: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  404: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

function statusBadgeClass(status) {
  return STATUS_STYLES[status] || 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';
}

export function BrosPdfReachabilityPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosPdfReachability();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const allOk = result && result.all_reachable;
  const hasError = result && result.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS PDF Reachability</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Samples invoices from the Finance Agent DB and probes each against the
          BR OS PDF endpoint. Confirms the stored invoice_id is the correct key
          before flipping the View source.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          Run Reachability Check
        </Button>

        {hasError && (
          <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-600" />
              <span className="text-sm text-red-800 dark:text-red-200">{result.error}</span>
            </div>
          </div>
        )}

        {result && !hasError && (
          <div className="space-y-3">
            <div className={`p-3 rounded-lg border ${allOk ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950' : 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950'}`}>
              <div className="flex items-center gap-2">
                {allOk
                  ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : <AlertTriangle className="h-4 w-4 text-red-600" />
                }
                <span className="text-sm font-medium">
                  {allOk
                    ? `All ${result.sample_count} sampled invoices returned 200 from BR OS.`
                    : `Some sampled invoices failed. Do NOT proceed to flip the View until all return 200.`
                  }
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Total BROS</p>
                <p className="text-lg font-semibold tabular-nums">{result.total_bros_invoices}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">212xxx</p>
                <p className="text-lg font-semibold tabular-nums">{result.pattern_counts?.['212xxx'] ?? '-'}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">INV-xxx</p>
                <p className="text-lg font-semibold tabular-nums">{result.pattern_counts?.['INV-xxx'] ?? '-'}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Other</p>
                <p className="text-lg font-semibold tabular-nums">{result.pattern_counts?.other ?? '-'}</p>
              </div>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">invoice_id</th>
                    <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                    <th className="text-left px-2 py-1.5 font-medium">Diverges?</th>
                    <th className="text-left px-2 py-1.5 font-medium">BR OS Status</th>
                    <th className="text-left px-2 py-1.5 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.samples.map((row, i) => (
                    <tr key={row.invoice_id || i} className="border-b last:border-0">
                      <td className="px-2 py-1 font-mono text-[10px]">{row.invoice_id || '-'}</td>
                      <td className="px-2 py-1 font-mono">{row.invoice_number || '-'}</td>
                      <td className="px-2 py-1">
                        {row.diverges
                          ? <Badge className="text-[10px] bg-amber-100 text-amber-700">yes</Badge>
                          : <span className="text-muted-foreground">no</span>
                        }
                      </td>
                      <td className="px-2 py-1">
                        {row.bros_http_status != null
                          ? <Badge className={`text-[10px] ${statusBadgeClass(row.bros_http_status)}`}>{row.bros_http_status}</Badge>
                          : <span className="text-muted-foreground">-</span>
                        }
                      </td>
                      <td className="px-2 py-1 text-[10px] text-muted-foreground max-w-[200px] truncate">
                        {row.error_detail || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
